import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import {
  addNote,
  fetchAccountTransactionInfo,
  IAccountTransactionInfoResponse,
  INoteAddRequest,
} from '@/api/AccountApi'
import * as Bridge from '@/api/BridgeApi'
import { getCSRF } from '@/api/CsrfApi'
import { EmptyAccountTransaction } from '@/domains/AccountDomain'
import { IDebitCardTransactionDetail } from '@/domains/DebitCardDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { createFetchAction, createPostAction, createSaga } from '@/utils/actionUtils'
import { ContentType } from '@/utils/bridgeUtils/features/shareContent'
import { apiFailureReducer, reset } from '@/utils/reducerUtils'
import { getShareImageContents } from '@/utils/shareUtils'

export interface IDebitCardHistoryDetailView {
  isEditMode: boolean
  note: string
}

export interface IDebitCardHistoryDetailState extends IAccountTransactionInfoResponse {
  view: IDebitCardHistoryDetailView
}

const defaultViewState = {
  isEditMode: false,
  note: '',
}

const initialState: IDebitCardHistoryDetailState = {
  ...EmptyAccountTransaction,
  view: defaultViewState,
}

export const DEBIT_CARD_HISTORY_DETAIL = 'DebitCardHistoryDetail'
export const ADD_NOTE = `${DEBIT_CARD_HISTORY_DETAIL}/addNote`
export const SET_VIEW = `${DEBIT_CARD_HISTORY_DETAIL}/setView`
export const CLEAR_STATE = `${DEBIT_CARD_HISTORY_DETAIL}/clearState`
export const SHARE_CONTENTS = `${DEBIT_CARD_HISTORY_DETAIL}/shareContents`

export const debitCardHistoryDetailActions = {
  setView: createAction(SET_VIEW),
  clearState: createAction(CLEAR_STATE),
  shareContents: createAction(SHARE_CONTENTS),
}

export const debitCardHistoryDetailAsync = createFetchAction(DEBIT_CARD_HISTORY_DETAIL)
export const addTransactionNoteAsync = createPostAction(ADD_NOTE)

export const reducer = {
  [debitCardHistoryDetailAsync.REQUEST]: (
    state: IDebitCardHistoryDetailState,
    action: Action<IAccountTransactionInfoResponse>,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [debitCardHistoryDetailAsync.SUCCESS]: (
    state: IDebitCardHistoryDetailState,
    action: Action<IAccountTransactionInfoResponse>,
  ) => ({
    ...state,
    ...action.payload,
    view: {
      ...state.view,
      note: action.payload.trxNote,
    },
  }),
  [debitCardHistoryDetailAsync.FAILURE]: apiFailureReducer,
  [SET_VIEW]: (state: IDebitCardHistoryDetailState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_STATE]: (state: IDebitCardHistoryDetailState) => reset(state, initialState),
}

export const debitCardHistoryDetailSelector = {
  detail: (state: IRootState): IDebitCardTransactionDetail => {
    const {
      tranAfJan,
      tranTrDtm,
      tranTrCd,
      hisNo,
      trxNote,
      tranTrAmt,
      detail,
    }: IDebitCardHistoryDetailState = state[DEBIT_CARD_HISTORY_DETAIL]

    return {
      acctNo: detail.acctNo,
      acctTitle: detail.acctTitle,
      tranTrAmt,
      trxOriAmt: detail.trxOriAmt,
      tranAfJan,
      bfInt: detail.bfInt,
      tranTrCd,
      bnftFee: detail.bnftFee,
      trxOriCcy: detail.trxOriCcy,
      hisNo,
      trxMerchantNm: detail.trxMerchantNm,
      trxNote,
      tranTrDtm,
      trxApprDtm: detail.trxApprDtm,
      trxAcctNm: detail.trxAcctNm,
      trxBnkNm: detail.trxBnkNm,
      trxAcctNo: detail.trxAcctNo,
      tselSerialNum: detail.tselSerialNum,
    }
  },
  view: (state: IRootState) => oc(state[DEBIT_CARD_HISTORY_DETAIL]).view(defaultViewState),
}

export function* addTransactionNote(action: Action<INoteAddRequest>) {
  yield put(debitCardHistoryDetailActions.setView({ isEditMode: false }))
  const params = action.payload

  try {
    yield call(getCSRF)
    yield call(addNote, params)
  } catch (e) {
    const { trxNote } = yield select(debitCardHistoryDetailSelector.detail)

    yield put(debitCardHistoryDetailActions.setView({ note: trxNote }))
    yield put(debitCardHistoryDetailAsync.failure(e))
    yield put(errorActions.set(e))
  }
}

export function* shareContents(action: Action<HTMLElement>) {
  const element = action.payload

  try {
    const data = yield call(getShareImageContents, element)

    yield call(Bridge.shareContent, { type: ContentType.IMAGE, data })
  } catch (e) {
    errorActions.set(e)
  }
}

export const debitCardHistoryDetailReducer = handleActions(reducer, initialState)
export const debitCardHistoryDetailSaga = [
  takeLatest(
    debitCardHistoryDetailAsync.FETCH,
    createSaga(debitCardHistoryDetailAsync, fetchAccountTransactionInfo),
  ),
  takeLatest(addTransactionNoteAsync.POST, addTransactionNote),
  takeLatest(SHARE_CONTENTS, shareContents),
]
